import { isValidString } from '@afterpay/utils'
import clsx from 'clsx'
import React, { forwardRef, FunctionComponent } from 'react'

import { ColorTypes, getColorClassName } from '../foundation/Colors'
import { useDesignLanguagePerClass, useIsDesignLanguage, useThemeClass } from '../Theme'
import { DesignLanguage } from '../Theme/types'
import { TypoFontFace } from '../Typography/types'
import { noop } from '../utils/function'
import { KINDS, SIZES } from './constants'
import styles from './style.module.scss'

type CSSClasses = keyof typeof styles

type SizesType = typeof SIZES
type SizesKeys = keyof SizesType
export type LinkSizes = SizesType[SizesKeys]

type KindsType = typeof KINDS
type KindsKeys = keyof KindsType
export type LinkKinds = KindsType[KindsKeys]

interface BaseLinkProps {
  size?: LinkSizes
  kind?: LinkKinds
  color?: ColorTypes
  bold?: boolean
  disabled?: boolean
  inline?: boolean
  testNameSpace?: string
  fontFace?: TypoFontFace
  'data-dd-action-name'?: string
}

export type AnchorLinkProps = BaseLinkProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseLinkProps | 'style'> & {
    href: string
  }

export type ButtonLinkProps = BaseLinkProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseLinkProps | 'style'> & {
    href?: never
  }

export type LinkProps = AnchorLinkProps | ButtonLinkProps

export type LinkComponent = FunctionComponent<LinkProps>

export const Link = forwardRef<HTMLAnchorElement | HTMLButtonElement, LinkProps>(
  (
    { size, kind = KINDS.EMPHASIS, color, bold = true, children, testNameSpace = '', tabIndex = 0, disabled = false, inline = false, fontFace, ...otherProps },
    ref
  ) => {
    const sizeClassName = typeof size !== 'undefined' && styles[`size${size.toUpperCase()}` as CSSClasses]
    const kindClassName = clsx(styles.kindBase, styles[`kind${kind}`])
    const colorClassName = typeof color !== 'undefined' && styles[getColorClassName(color) as CSSClasses]
    const themeClass = useThemeClass()
    const designLanguageStyles = useDesignLanguagePerClass(styles)
    const isArcade = useIsDesignLanguage(DesignLanguage.Arcade)

    // CashSansMono is Arcade-only, ignore it for other design languages
    const effectiveFontFace = fontFace === 'CashSansMono' && !isArcade ? undefined : fontFace

    const className = clsx(
      kindClassName,
      sizeClassName,
      disabled && styles.disabled,
      colorClassName,
      !bold && styles.regular,
      inline && styles.inline,
      effectiveFontFace && styles[`fontFace${effectiveFontFace}`],
      styles[`${themeClass}`],
      designLanguageStyles.link
    )

    const hasTestNameSpace = testNameSpace.length > 0
    const hasDatadogActionName = typeof otherProps['data-dd-action-name'] !== 'undefined'
    const datadogActionName = hasDatadogActionName ? otherProps['data-dd-action-name'] : hasTestNameSpace ? `${testNameSpace} Anchor` : 'Anchor'

    // Render anchor tag if href exists
    const hasHref = isValidString(otherProps.href)
    const HtmlTag = hasHref ? 'a' : 'button'

    const attributes = {
      ...otherProps,
      className,
      onClick: disabled ? noop : otherProps.onClick,
      tabIndex,
      ref: ref as React.Ref<HTMLAnchorElement | HTMLButtonElement>,
      'data-dd-action-name': datadogActionName,
      ...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-anchor` }),
    }

    const props = hasHref ? attributes : { ...attributes, disabled, type: 'button' }

    return <HtmlTag {...(props as React.HTMLAttributes<HTMLElement>)}>{children}</HtmlTag>
  }
)

Link.displayName = 'Link'
